import { useContext, useMemo } from 'react'

import { useCollaborationUpsell } from '@/collaboration/hooks/useCollaborationUpsell'
import { CollaborationOrgPromptPopover } from '@/collaboration/components/CollaborationOrgPromptPopover'
import { AvatarStack, type AvatarData } from '@/components/avatar-stack/AvatarStack'
import { Button } from '@/components/button/Button'
import { IconButton } from '@/components/icon-button/IconButton'
import { AddCollaborator, Lock } from '@/assets/icons'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { MattersActions, MatterDialogType } from '@/matter/store/slice'
import { selectActiveMatter, selectMatterMembers } from '@/matter/store/selectors'
import { getMemberInitials, getMemberDisplayName } from '@/collaboration/utils/members'
import { canEditMatterMembers, canViewMatterMembers } from '@/matter/utils/matter-permissions'
import { AuthContext } from '@/context/auth-context'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { MatterCollaborationEntrySource } from '@/analytics/enums/matter-collaboration.enums'
import { getCollaborationOrganizationId } from '@/analytics/utils/matter-collaboration-analytics'

export function CollaborationHeaderActions() {
  const { showUpsell, handleUpsellClick } = useCollaborationUpsell()

  const dispatch = useAppDispatch()
  const { trackEvent } = useAnalytics()
  const authContext = useContext(AuthContext)
  const matter = useAppSelector(selectActiveMatter)

  const rawMembers = useAppSelector((state) => selectMatterMembers(state, canViewMatterMembers(matter) ? matter?.id : undefined))
  // Only accepted members get an avatar. The owner qualifies by role because the backend leaves their accepted_at unset.
  const members: AvatarData[] = useMemo(
    () =>
      rawMembers
        .filter((member) => member.matter_role === 'owner' || member.accepted_at != null)
        .map((member) => ({ userId: member.user_id, initials: getMemberInitials(member), name: getMemberDisplayName(member) })),
    [rawMembers]
  )

  if (showUpsell) {
    return (
      <div className="flex items-center">
        {/* mobile: lock icon button */}
        <CollaborationOrgPromptPopover onCreateOrganization={handleUpsellClick}>
          <IconButton icon={<Lock className="h-4 w-4" />} variant="tonal" size="sm" aria-label="Collaborate on this matter" className="sm:hidden" />
        </CollaborationOrgPromptPopover>
        {/* desktop: text button with lock */}
        <CollaborationOrgPromptPopover onCreateOrganization={handleUpsellClick}>
          <Button variant="tonal" color="secondary" size="sm" trailingIcon={<Lock className="h-4 w-4" />} className="max-sm:hidden sm:flex">
            Add Members
          </Button>
        </CollaborationOrgPromptPopover>
      </div>
    )
  }

  const openManageMembersDialog = (source: MatterCollaborationEntrySource) => {
    if (!matter) return
    dispatch(
      MattersActions.openDialog({
        dialogType: MatterDialogType.ManageMembers,
        matterId: matter.id,
        source,
      })
    )
  }

  // Avatar stack is a pre-existing "view members" affordance — never fire the Add Members
  // discoverability click, and attribute resulting invites to Avatar (not Header).
  const handleViewMembers = () => {
    openManageMembersDialog(MatterCollaborationEntrySource.Avatar)
  }

  const handleAddMembers = () => {
    if (!matter) return
    const organizationId = getCollaborationOrganizationId(authContext?.userAccountData)
    trackEvent(AnalyticsEvent.MatterCollaborationHeaderAddUserClicked, { matterId: matter.id, organizationId })
    openManageMembersDialog(MatterCollaborationEntrySource.Header)
  }

  const canAdd = canEditMatterMembers(matter)

  return (
    <div className="flex items-center gap-2">
      {/* on desktop, show the avatar stack */}
      <div className="hidden sm:flex">
        <AvatarStack avatars={members} onClick={handleViewMembers} />
      </div>
      {canAdd && (
        <>
          {/* on mobile, show the icon button */}
          <IconButton
            icon={<AddCollaborator className="h-4 w-4" />}
            variant="outlined"
            size="sm"
            aria-label="Manage members"
            onClick={handleAddMembers}
            className="sm:hidden"
          />
          {/* on desktop, show the button with text */}
          <Button
            variant="outlined"
            color="primary"
            size="sm"
            trailingIcon={<AddCollaborator className="h-4 w-4" />}
            onClick={handleAddMembers}
            className="max-sm:hidden sm:flex"
          >
            Add Members
          </Button>
        </>
      )}
    </div>
  )
}
